/**
 * One keyword audit's state as it streams in — whichever transport is feeding it.
 *
 * The public page runs a fresh audit over `streamAudit`; the admin Discovery page
 * replays a stored one. Both deliver the same frames through the same `AuditHandlers`,
 * so the tables they render cannot disagree about what a row, a support count or a
 * recommendation means.
 *
 * ROWS ARRIVE OUT OF ORDER (whichever keyword resolves first), which is why `plan`
 * exists as its own frame: it fixes the row order up front so the table does not
 * reshuffle itself while somebody is reading it.
 */
import { ApiError } from '@api/errors';
import type { AuditHandlers, Community, Recommendation, Standing } from '../audit';

export class AuditRun {
  community = $state<Community | null>(null);
  plan = $state<string[]>([]);
  rows = $state<Record<string, Standing>>({});
  // Competitor counts for the audited keywords. Arrives as its own frame after the
  // rows, so the "Competitors" column fills in a moment after the table does.
  support = $state<Record<string, number>>({});
  recs = $state<{ high_volume: Recommendation[]; winnable: Recommendation[] } | null>(null);
  auditId = $state<number | null>(null);
  busy = $state(false);
  error = $state<string | null>(null);

  done = $derived(Object.keys(this.rows).length);
  // Every ranking is on screen. That — not the recommendations — is when the reader is
  // finished with the ranking step, so it is what opens the way to the next one. Stage
  // two's own work carries on in the background and the improve step shows its own wait
  // if it is still running when they arrive.
  allRowsIn = $derived(
    this.done === this.plan.length && (this.plan.length > 0 || this.recs !== null),
  );

  // The ranking table's row order: biggest field first, with rows still loading held at
  // the bottom in the order they were planned.
  //
  // Pending rows sink rather than sorting as zero, which would put every unfinished row
  // at the bottom anyway but claim a field size it does not have. Sorting only what has
  // landed means a row arrives, takes its place, and nothing already read moves — the
  // `(keyword)` key lets Svelte move the DOM node instead of rebuilding it.
  orderedPlan = $derived.by(() => {
    const landed = this.plan.filter((k) => this.rows[k]);
    landed.sort((a, b) => (this.rows[b]?.n_hits ?? 0) - (this.rows[a]?.n_hits ?? 0));
    return [...landed, ...this.plan.filter((k) => !this.rows[k])];
  });

  // The finished rows in plan order, best rank first and unranked last. The table
  // renders `plan` directly (so skeleton rows hold their place while streaming); this
  // is for the improve step, which only ever sees completed rows.
  standings = $derived(
    this.plan
      .map((k) => this.rows[k])
      .filter((r): r is Standing => r !== undefined)
      .sort((a, b) => {
        if (a.rank === null && b.rank === null) return b.n_hits - a.n_hits;
        if (a.rank === null) return 1;
        if (b.rank === null) return -1;
        return a.rank - b.rank;
      }),
  );

  started = $derived(this.plan.length > 0 || this.busy);

  reset(): void {
    this.error = null;
    this.community = null;
    this.plan = [];
    this.rows = {};
    this.recs = null;
    this.support = {};
    this.auditId = null;
  }

  async run(source: (handlers: AuditHandlers) => Promise<void>): Promise<void> {
    this.reset();
    this.busy = true;
    try {
      await source({
        onCommunity: (c) => {
          this.community = c;
        },
        onPlan: (ks) => {
          this.plan = ks;
        },
        // Reassigned rather than mutated: the table must re-render on every row that
        // lands, and a fresh object is what says so unambiguously.
        onRow: (row) => {
          this.rows = { ...this.rows, [row.keyword]: row };
        },
        onSupport: (counts) => {
          this.support = counts;
        },
        onRecommendations: (r) => {
          this.recs = { high_volume: r.high_volume, winnable: r.winnable };
        },
        onDone: (id) => {
          this.auditId = id;
        },
      });
    } catch (err: unknown) {
      this.error =
        err instanceof ApiError
          ? err.status === 429
            ? 'That is a few audits in a row — give it a minute and try again.'
            : err.message
          : 'Something went wrong. Try again in a moment.';
    } finally {
      this.busy = false;
    }
  }
}
